import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useLang } from "../lib/lang";
import { lockScroll } from "../lib/smooth";

export const ProjectModal = ({ project, onClose }) => {
  const { tr } = useLang();

  useEffect(() => {
    lockScroll(!!project);
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); lockScroll(false); };
  }, [project, onClose]);

  return (
    <AnimatePresence>
      {project && (
        <motion.div
          data-testid="project-modal"
          className="fixed inset-0 z-[80] overflow-y-auto bg-[var(--ink)]"
          data-lenis-prevent
          initial={{ clipPath: "inset(100% 0 0 0)" }}
          animate={{ clipPath: "inset(0% 0 0 0)" }}
          exit={{ clipPath: "inset(0 0 100% 0)" }}
          transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
        >
          <button data-testid="project-modal-close-btn" onClick={onClose} className="fixed right-4 top-4 z-10 grid h-12 w-12 place-items-center rounded-full bg-[var(--acid)] text-[var(--ink)] transition-transform hover:rotate-90 sm:right-8 sm:top-8" aria-label="close">
            <X size={20} />
          </button>
          <div className="mx-auto max-w-6xl px-4 pb-24 pt-24 sm:px-8">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[var(--acid)]">{tr(project.client)}</p>
            <h2 data-testid="project-modal-title" className="mt-4 font-display text-5xl font-extrabold uppercase leading-[0.9] tracking-tight sm:text-7xl lg:text-8xl">{tr(project.title)}</h2>
            <div data-testid="project-modal-content" className="mt-14 grid gap-10 border-t border-white/10 pt-10 md:grid-cols-3">
              <div>
                <h4 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">{tr({ es: "Resumen", en: "Summary" })}</h4>
                <p className="mt-3 text-zinc-200">{tr(project.summary)}</p>
              </div>
              <div>
                <h4 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">{tr({ es: "Estrategia", en: "Strategy" })}</h4>
                <p className="mt-3 text-zinc-200">{tr(project.strategy)}</p>
              </div>
              <div>
                <h4 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">{tr({ es: "Resultados", en: "Results" })}</h4>
                <ul className="mt-3 space-y-2">
                  {tr(project.results).map((r) => (
                    <li key={r} className="flex gap-3 text-zinc-200"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--acid)]" />{r}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="mt-16 columns-1 gap-6 sm:columns-2">
              {project.video && (
                <div className="stage-spot mb-6 break-inside-avoid overflow-hidden rounded-2xl ring-1 ring-white/10">
                  <video data-testid="project-modal-video" src={project.video} poster={project.video.replace(".mp4", ".jpg")} autoPlay muted loop playsInline controls className="w-full" />
                </div>
              )}
              {project.gallery.map((src, i) => (
                <motion.div
                  key={src}
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + i * 0.08, duration: 0.8 }}
                  className="stage-spot mb-6 break-inside-avoid overflow-hidden rounded-2xl ring-1 ring-white/10"
                >
                  <img src={src} alt={`${tr(project.title)} ${i + 1}`} loading="lazy" className="w-full" />
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
